import React, { useEffect, useState } from "react";
import { getMakeupArtists, createMakeupArtist, updateMakeupArtist, deleteMakeupArtist } from "../../services/makeupArtistService";
import { getArtistTypes } from "../../services/artistTypeService";

interface MakeupArtist {
  makeupArtistId: number;
  displayName: string;
  bio?: string;
  active: boolean;
  artistTypeId?: number;
  artistTypeName?: string;
}

export default function MakeupArtists() {
  const [artists, setArtists] = useState<MakeupArtist[]>([]);
  const [artistTypes, setArtistTypes] = useState<{ artistTypeId: number; artistTypeName: string }[]>([]);
  const [form, setForm] = useState<Partial<MakeupArtist>>({ active: true });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadArtists = async () => {
    try {
      setLoading(true);
      const [artistData, typeData] = await Promise.all([
        getMakeupArtists(),
        getArtistTypes()
      ]);
  setArtists(artistData);
  setArtistTypes(typeData);
    } catch (err: any) {
      setError("Failed to load artists/types. " + (err?.response?.data?.message || err?.message || ""));
  console.error("Load artists/types error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArtists();
  }, []);

  const handleSave = async () => {
    if (!form.displayName || form.active === undefined || !form.artistTypeId) {
      setError("Display Name, Active, and Artist Type are required.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      if (form.makeupArtistId) {
        await updateMakeupArtist(form.makeupArtistId, form);
      } else {
        await createMakeupArtist(form);
      }
      setForm({ active: true });
      setEditingId(null);
      await loadArtists();
    } catch (err: any) {
      setError("Failed to save artist. " + (err?.response?.data?.message || err?.message || ""));
  console.error("Save artist error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (a: MakeupArtist) => {
    setEditingId(a.makeupArtistId);
    setForm({
      makeupArtistId: a.makeupArtistId,
      displayName: a.displayName,
      bio: a.bio || "",
      active: a.active,
      artistTypeId: a.artistTypeId || undefined
    });
  };

  const handleDelete = async (makeupArtistId: number) => {
    if (!window.confirm("Delete this artist?")) return;
    try {
      setLoading(true);
      await deleteMakeupArtist(makeupArtistId);
      await loadArtists();
    } catch (err: any) {
      setError("Failed to delete artist. " + (err?.response?.data?.message || err?.message || ""));
  console.error("Delete artist error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Manage Artists</h2>

      {/* Add/Edit Form */}
      <div className="mb-6 bg-gray-50 p-4 rounded shadow">
        <h3 className="font-semibold mb-2">{editingId ? "Edit" : "Add"} Artist</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Display Name"
            value={form.displayName || ""}
            onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
            className="border rounded px-3 py-2"
          />
          <input
            type="text"
            placeholder="Bio"
            value={form.bio || ""}
            onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
            className="border rounded px-3 py-2"
          />
          <select
            value={form.artistTypeId || ""}
            onChange={e => setForm(f => ({ ...f, artistTypeId: Number(e.target.value) }))}
            className="border rounded px-3 py-2"
          >
            <option value="">Select Artist Type</option>
            {artistTypes.map(type => (
              <option key={type.artistTypeId} value={type.artistTypeId}>{type.artistTypeName}</option>
            ))}
          </select>
          <select
            value={String(form.active)}
            onChange={e => setForm(f => ({ ...f, active: e.target.value === "true" }))}
            className="border rounded px-3 py-2"
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            {editingId ? "Update" : "Add"}
          </button>
          {editingId && (
            <button
              onClick={() => { setForm({ active: true }); setEditingId(null); }}
              className="bg-gray-300 px-4 py-2 rounded"
            >Cancel</button>
          )}
        </div>
        {error && <p className="text-red-500 mt-2">{error}</p>}
      </div>

      {/* List */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="w-full border border-gray-200 bg-white rounded-lg shadow-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Display Name</th>
              <th className="px-4 py-2 text-left">Bio</th>
              <th className="px-4 py-2 text-left">Artist Type</th>
              <th className="px-4 py-2 text-left">Active</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {artists.map(a => (
              <tr key={a.makeupArtistId} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2">{a.displayName}</td>
                <td className="px-4 py-2">{a.bio || '-'}</td>
                <td className="px-4 py-2">{a.artistTypeName || '-'}</td>
                <td className="px-4 py-2">{a.active ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => handleEdit(a)}
                    className="text-blue-500 hover:text-blue-700 mr-2"
                  >Edit</button>
                  <button
                    onClick={() => handleDelete(a.makeupArtistId)}
                    className="text-red-500 hover:text-red-700"
                  >Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
